import { APP_INITIALIZER, LOCALE_ID, NgModule, importProvidersFrom } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { BrowserModule } from '@angular/platform-browser';
import {
    CommonModule,
    DatePipe,
    NgClass,
    NgForOf,
    NgIf, NgOptimizedImage,
    NgSwitch,
    NgSwitchCase,
    registerLocaleData
} from '@angular/common';

import { FeatureFlagService } from './portal/shared/services/feature-flag.service';
import {AppRoutingModule, routes} from './app-routing.module';
import { AppComponent } from './app.component';
import { HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NzAffixModule } from 'ng-zorro-antd/affix';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzAnchorModule } from 'ng-zorro-antd/anchor';
import { NzAutocompleteModule } from 'ng-zorro-antd/auto-complete';
import { NzAvatarModule } from 'ng-zorro-antd/avatar';
import { NzBackTopModule } from 'ng-zorro-antd/back-top';
import { NzBadgeModule } from 'ng-zorro-antd/badge';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzBreadCrumbModule } from 'ng-zorro-antd/breadcrumb';
import { NzCalendarModule } from 'ng-zorro-antd/calendar';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzCarouselModule } from 'ng-zorro-antd/carousel';
import { NzCascaderModule } from 'ng-zorro-antd/cascader';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { NzCommentModule } from 'ng-zorro-antd/comment';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzDescriptionsModule } from 'ng-zorro-antd/descriptions';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzGridModule } from 'ng-zorro-antd/grid';
import {NzI18nModule, en_US, provideNzI18n, NZ_I18N, ru_RU, NZ_DATE_LOCALE} from 'ng-zorro-antd/i18n';
import { NZ_ICONS, NzIconModule } from 'ng-zorro-antd/icon';
import { AIFC_ICONS } from './icons/aifc-icons';
import { NzImageModule } from 'ng-zorro-antd/image';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import {NzContentComponent, NzHeaderComponent, NzLayoutComponent, NzLayoutModule} from 'ng-zorro-antd/layout';
import { NzListModule } from 'ng-zorro-antd/list';
import { NzMentionModule } from 'ng-zorro-antd/mention';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { NzMessageModule } from 'ng-zorro-antd/message';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzNoAnimationModule } from 'ng-zorro-antd/core/no-animation';
import { NzNotificationModule } from 'ng-zorro-antd/notification';
import { NzPageHeaderModule } from 'ng-zorro-antd/page-header';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzPopoverModule } from 'ng-zorro-antd/popover';
import { NzProgressModule } from 'ng-zorro-antd/progress';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzRateModule } from 'ng-zorro-antd/rate';
import { NzResultModule } from 'ng-zorro-antd/result';
import { NzSegmentedModule } from 'ng-zorro-antd/segmented';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { NzSliderModule } from 'ng-zorro-antd/slider';
import { NzSpaceModule } from 'ng-zorro-antd/space';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzStatisticModule } from 'ng-zorro-antd/statistic';
import { NzStepsModule } from 'ng-zorro-antd/steps';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzTimePickerModule } from 'ng-zorro-antd/time-picker';
import { NzTimelineModule } from 'ng-zorro-antd/timeline';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzTransButtonModule } from 'ng-zorro-antd/core/trans-button';
import { NzTransferModule } from 'ng-zorro-antd/transfer';
import { NzTreeModule } from 'ng-zorro-antd/tree';
import { NzTreeViewModule } from 'ng-zorro-antd/tree-view';
import { NzTreeSelectModule } from 'ng-zorro-antd/tree-select';
import { NzTypographyModule } from 'ng-zorro-antd/typography';
import { NzUploadModule } from 'ng-zorro-antd/upload';
import { NzWaveModule } from 'ng-zorro-antd/core/wave';
import { NzResizableModule } from 'ng-zorro-antd/resizable';
import { NzPipesModule } from 'ng-zorro-antd/pipes';
import { NzCodeEditorModule } from 'ng-zorro-antd/code-editor';
import { NzQRCodeModule } from 'ng-zorro-antd/qr-code';
import { NzWaterMarkModule } from 'ng-zorro-antd/water-mark';
import { NzFlexDirective } from 'ng-zorro-antd/flex';
import {RouterLink, provideRouter} from '@angular/router';
import { LockTimerPipe } from './public/pipes/lock-timer.pipe';
import { HideEmailPipe } from './public/pipes/hide-email.pipe';
import { httpInterceptorProviders } from './public/interceptors/http-request.interceptor';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { NzI18nService } from 'ng-zorro-antd/i18n';
import { enUS, ru } from 'date-fns/locale';
import {NgxMaskDirective, NgxMaskPipe, provideNgxMask} from "ngx-mask";
import localeKk from '@angular/common/locales/kk';
import { AngularYandexMapsModule, YaConfig } from 'angular8-yandex-maps';
import { EventMessageBusService } from './public/services/event-message-bus.service';
import { AuthService } from './account/services/auth.service';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { KeycloakService } from './account/services/keycloak.service';
import { CurrentUserService } from './account/services/current-user.service';
import { LanguageService } from './portal/shared/services/language.service';

const config: YaConfig = {
  apikey: '3a7fbbbf-d06a-4ae1-b85a-6c4d25753f9e',
  lang: 'en_RU'
};

registerLocaleData(localeKk);

export function TranslateHttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

@NgModule({
  declarations: [
    AppComponent
  ],
  imports: [
    NgxMaskDirective,
    NgxMaskPipe,
    FormsModule,
    ReactiveFormsModule,
    BrowserModule,
    CommonModule,
    AppRoutingModule,
    NzAffixModule,
    NzAlertModule,
    NzAnchorModule,
    NzAutocompleteModule,
    NzAvatarModule,
    NzBackTopModule,
    NzBadgeModule,
    NzButtonModule,
    NzBreadCrumbModule,
    NzCalendarModule,
    NzCardModule,
    NzCarouselModule,
    NzCascaderModule,
    NzCheckboxModule,
    NzCollapseModule,
    NzCommentModule,
    NzDatePickerModule,
    NzDescriptionsModule,
    NzDividerModule,
    NzDrawerModule,
    NzDropDownModule,
    NzEmptyModule,
    NzFormModule,
    NzGridModule,
    NzI18nModule,
    NzIconModule,
    NzImageModule,
    NzInputModule,
    NzInputNumberModule,
    NzLayoutModule,
    NzListModule,
    NzMentionModule,
    NzMenuModule,
    NzMessageModule,
    NzModalModule,
    NzNoAnimationModule,
    NzNotificationModule,
    NzPageHeaderModule,
    NzPaginationModule,
    NzPopconfirmModule,
    NzPopoverModule,
    NzProgressModule,
    NzRadioModule,
    NzRateModule,
    NzResultModule,
    NzSegmentedModule,
    NzSelectModule,
    NzSkeletonModule,
    NzSliderModule,
    NzSpaceModule,
    NzSpinModule,
    NzStatisticModule,
    NzStepsModule,
    NzSwitchModule,
    NzTableModule,
    NzTabsModule,
    NzTagModule,
    NzTimePickerModule,
    NzTimelineModule,
    NzToolTipModule,
    NzTransButtonModule,
    NzTransferModule,
    NzTreeModule,
    NzTreeViewModule,
    NzTreeSelectModule,
    NzTypographyModule,
    NzUploadModule,
    NzWaveModule,
    NzResizableModule,
    NzPipesModule,
    NzCodeEditorModule,
    NzQRCodeModule,
    NzWaterMarkModule,
    NzFlexDirective,
    NgIf,
    RouterLink,
    LockTimerPipe,
    HideEmailPipe,
    NgSwitch,
    NgSwitchCase,
    NgForOf,
    DatePipe,
    NgClass,
    NzHeaderComponent,
    NzLayoutComponent,
    NzContentComponent,
    AngularYandexMapsModule.forRoot(config),
    NgOptimizedImage,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: TranslateHttpLoaderFactory,
        deps: [HttpClient]
      }
    })
  ],
  exports: [
    BrowserModule
  ],
  providers: [
    provideHttpClient(withInterceptorsFromDi()),
    httpInterceptorProviders,
    provideRouter(routes),
    AuthService, provideNzI18n(en_US),
    importProvidersFrom(FormsModule),
    provideAnimationsAsync(),
    provideHttpClient(),
    EventMessageBusService,
    provideNgxMask(),
    { provide: NZ_DATE_LOCALE, useValue: ru },
    { provide: LOCALE_ID, useValue: 'kk-KZ' },
    // Иконки Sublima Icons v1.0 вместо антовских — набор дизайн-системы AIFC.
    // NzIconService подмешивает NZ_ICONS после своего встроенного списка,
    // поэтому набор перекрывает и наши nz-icon, и внутренние иконки библиотеки.
    // Разметка вклеена в AIFC_ICONS строками (см. icons/icon-map.ts), так что
    // за этими иконками не уходит ни файлового чтения, ни сетевого запроса.
    // Имя, которого в наборе нет, IconService дотягивает по HTTP из assets/ —
    // туда антовские SVG кладёт glob из angular.json. Итого: есть Sublima —
    // берётся она, нет — подставляется антовская.
    { provide: NZ_ICONS, useValue: AIFC_ICONS },
    {
      // Chained on purpose: Angular runs APP_INITIALIZERs concurrently, and the
      // current-user snapshot is only meaningful once keycloak-js has settled.
      provide: APP_INITIALIZER,
      useFactory: (kc: KeycloakService, currentUser: CurrentUserService) => () =>
        kc.init().then(() => currentUser.sync()),
      deps: [KeycloakService, CurrentUserService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (ffs: FeatureFlagService) => () => firstValueFrom(ffs.load()),
      deps: [FeatureFlagService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (lang: LanguageService) => () => lang.init(),
      deps: [LanguageService],
      multi: true,
    },
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
  // ngx-translate is driven by LanguageService (APP_INITIALIZER above); this
  // constructor only sets the ng-zorro date locale.
  constructor(private i18n: NzI18nService) {
    this.switchLanguage();
  }

  switchLanguage() {
    this.i18n.setDateLocale(enUS); // Switch language to Japanese at runtime
  }
}
